import { IconVariant } from '@hungryroot/icons';
import { type BaseTag } from '@hungryroot/rest';

import { tags as TagId } from '../src/tags';

export const tagGroups = {
  meatProtein: 1,
  seafoodProtein: 2,
  plantProtein: 3,
  polarizing: 5,
  spiceLevel: 6,
  nutrition: 7,
  breakfast: 8,
  snack: 9,
  certificates: 10,
  flavor: 11,
  cuisine: 15,
  subDishType: 17,
  season: 19,
  prep: 20,
  polarizing2: 23,
  occasions: 24,
  tools: 25,
  values: 26,
  sweets: 27,
  kidsSnacks: 33,
  readyMade: 35,
  budget: 38,
  DinnerDishTypes: 39,
  LunchDishTypes: 41,
} as const;

export type TagGroups = (typeof tagGroups)[keyof typeof tagGroups];

/**
 * Notes on usage:
 *
 * Many files reference this fn
 *
 * - `NutritionSection.tsx` relies on `icon` being set for tags to render
 * - Many files require `name` to be set for selected tags to show up in the header. We should update these areas
 *   to rely on the API for `name` and `remark` (subtitle) instead of hardcoding them.
 */
export function getTagDetails(tag: Pick<BaseTag, 'id'>) {
  switch (tag.id) {
    case TagId.Fusion:
      return {
        name: 'Fusion',
      };
    case TagId.Korean:
      return {
        name: 'Korean',
      };
    case TagId.Japanese:
      return {
        name: 'Japanese',
      };
    case TagId.Chinese:
      return {
        name: 'Chinese',
      };
    case TagId.Thai:
      return {
        name: 'Thai',
      };
    case TagId.Italian:
      return {
        name: 'Italian',
      };
    case TagId.American:
      return {
        name: 'American',
      };
    case TagId.Mexican:
      return {
        name: 'Mexican',
      };
    case TagId.Mediterranean:
      return {
        name: 'Mediterranean',
      };
    case TagId.Indian:
      return {
        name: 'Indian',
      };
    case TagId.Grill:
      return {
        name: 'Grill',
        icon: IconVariant.Grill,
      };
    case TagId.Airfryer:
      return {
        name: 'Airfryer',
        icon: IconVariant.Airfryer,
      };
    case TagId.Microwave:
      return {
        name: 'Microwave',
        icon: IconVariant.Microwave,
      };
    case TagId.Blender:
      return {
        name: 'Blender',
        icon: IconVariant.Blender,
      };
    case TagId.Crockpot:
      return {
        name: 'Slow Cooker',
        icon: IconVariant.Crockpot,
      };
    case TagId.Stove:
      return {
        name: 'Stove',
        icon: IconVariant.StoveTop,
      };
    case TagId.Oven:
      return {
        name: 'Oven',
        icon: IconVariant.Oven,
      };
    case TagId.Onions:
      return {
        icon: IconVariant.Onion,
      };
    case TagId.Garlic:
      return {
        icon: IconVariant.Garlic,
      };
    case TagId.Mushrooms:
      return {
        icon: IconVariant.Mushrooms,
      };
    case TagId.Cilantro:
      return {
        icon: IconVariant.Cilantro,
      };
    case TagId.Beets:
      return {
        icon: IconVariant.Beets,
      };
    case TagId.Coconut:
      return {
        icon: IconVariant.Coconut,
      };
    case TagId.BellPeppers:
      return {
        icon: IconVariant.BellPepper,
      };
    case TagId.Tomatoes:
      return {
        icon: IconVariant.Tomatoes,
      };
    case TagId.Eggplants:
      return {
        icon: IconVariant.Eggplant,
      };
    case TagId.Potatoes:
      return {
        icon: IconVariant.Potatoes,
      };
    case TagId.CarbConscious:
      return {
        icon: IconVariant.LowCarb,
      };
    case TagId.LessSugar:
      return {
        icon: IconVariant.LowSugar,
      };
    case TagId.HighProtein:
      return {
        icon: IconVariant.HighProtein,
      };
    case TagId.LowSodium:
      return {
        icon: IconVariant.LowSodium,
      };
    case TagId.AntiInflamitory:
      return {
        icon: IconVariant.AntiInflamitory,
      };
    case TagId.GutFriendly:
      return {
        icon: IconVariant.GutFriendly,
      };
    case TagId.ImmunityBoosting:
      return {
        icon: IconVariant.Immunity,
      };
    case TagId.HighFiber:
      return {
        icon: IconVariant.HighFiber,
      };
    case TagId.LowFat:
      return {
        icon: IconVariant.LowFat,
      };
    case TagId.LowCholesterol:
      return {
        icon: IconVariant.LowCholesterol,
      };
    case TagId.LowFodmap:
      return {
        icon: IconVariant.LowFodmap,
      };
    case TagId.CalorieConscious:
      return {
        icon: IconVariant.CalorieConscious,
      };
    case TagId.Burritos:
      return {
        name: 'Burritos',
      };
    case TagId.Tamales:
      return {
        name: 'Tamales',
      };
    case TagId.Salads:
      return {
        name: 'Salads',
      };
    case TagId.Sandwiches:
      return {
        name: 'Sandwiches',
      };
    case TagId.Pizza:
      return {
        name: 'Pizza',
      };
    case TagId.Trays:
      return {
        name: 'Trays',
      };
    case TagId.Soups:
      return {
        name: 'Soups',
      };
    case TagId.Bowls:
      return {
        name: 'Bowls',
      };
    case TagId.OnlyLowSodium:
      return {
        name: 'I only eat low sodium meals (<560mg).',
      };

    default:
      return {};
  }
}
